import React, { useState } from 'react';
import Text from '../../components/Text.js';
import Button from '../../components/Button.js';
import Notes from '../../components/Notes.js';
import TextInput from '../../components/TextFields/TextInput.js';
import Files from '../Files/index.js';
import * as dates from "@simplybenefits/sb-dates";
import i18n from '../../localization/i18n.js';
import tokens from '../../localization/tokens.js';
import { helpers } from '@simplybenefits/sb-utils';

/**
 * @param data
 * @param handleChange
 * @param isMaster
 * @param accessType
 * @param id
 * @param dispatch
 * @param type
 * @param listFiles
 * @param createFolder
 * @param uploadFile
 * @param downloadFile
 * @param removeFile
 */
function Attachments({
  data,
  handleChange,
  isMaster = false,
  accessType,
  id,
  dispatch,
  type,
  listFiles,
  createFolder,
  uploadFile,
  downloadFile,
  removeFile
}) {
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column'
    },
    sectionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '25px',
      marginBottom: '10px'
    },
    noteBox: {
      display: 'flex',
      flex: 1,
      alignItems: 'flex-start',
      justifyContent: 'flex-end',
      marginBottom: 10
    }
  };
  const [showExternalNote, setShowExternalNote] = useState(false);
  const [currentExternalNote, setCurrentExternalNote] = useState('');
  const [showInternalNote, setShowInternalNote] = useState(false);
  const [currentInternalNote, setCurrentInternalNote] = useState('');

  /**
   * Saves internal or external note
   * @param noteType (externalNotes, internalNotes)
   */
  const _saveNote = noteType => {
    let newNote = {};
    if (noteType === 'externalNotes') {
      newNote = {
        noteID: data.externalNotes.length + 1,
        body: currentExternalNote,
        createdBy: accessType === 'Master' ? 'Agent' : helpers.capitalizeFirstLetter(accessType),
        dateAdded: dates.getUTC()
      };
      handleChange('externalNotes', [...data.externalNotes, newNote]);
      setCurrentExternalNote('');
      setShowExternalNote(false);
    }
    if (noteType === 'internalNotes') {
      newNote = {
        noteID: data.internalNotes.length + 1,
        body: currentInternalNote,
        createdBy: accessType === 'Master' ? 'Agent' : helpers.capitalizeFirstLetter(accessType),
        dateAdded: dates.getUTC()
      };
      handleChange('internalNotes', [...data.internalNotes, newNote]);
      setCurrentInternalNote('');
      setShowInternalNote(false);
    }
  };

  /**
   * Remove a note from a given note type
   * @param {*} noteType 
   * @param {*} noteID
   */
  const _removeNote = (noteType, noteID) => {
    if (noteType === 'externalNotes') {
      const updatedExternalNotes = data.externalNotes.filter(note => note.noteID !== noteID);
      handleChange('externalNotes', updatedExternalNotes);
    }
    if (noteType === 'internalNotes') {
      const updatedInternalNotes = data.internalNotes.filter(note => note.noteID !== noteID);
      handleChange('internalNotes', updatedInternalNotes);
    }
  };

  /**
   * Close note and current note to blank
   * @param {*} noteType (externalNotes, internalNotes)
   */
  const _closeNote = noteType => {
    if (noteType === 'externalNotes') {
      setCurrentExternalNote('');
      setShowExternalNote(false);
    }
    if (noteType === 'internalNotes') {
      setCurrentInternalNote('');
      setShowInternalNote(false);
    }
  };

  /**
   * 
   * @returns 
   */
  const _canAddExternalNotes = () => {
    if (accessType.toUpperCase() === 'ADMINISTRATOR') {
      return ['Pending Admin Review', 'Incomplete'].includes(data.status);
    }
    if (accessType.toUpperCase() === 'MEMBER') {
      return ['Pending Member Review', 'Incomplete'].includes(data.status);
    }
    return ['Pending Agent Review', 'Pending Admin Review', 'Pending Member Review', 'Incomplete', 'Suspended'].includes(data.status);
  };
  return /*#__PURE__*/React.createElement("div", {
    style: styles.container
  }, /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    style: styles.sectionHeader
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    medium: true
  }, i18n.t(tokens.labels.changeRequests.notes)), _canAddExternalNotes() && !showExternalNote && /*#__PURE__*/React.createElement(Button, {
    transparent: true,
    onClick: () => setShowExternalNote(true)
  }, i18n.t(tokens.labels.changeRequests.addNote))), showExternalNote && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TextInput, {
    fullWidth: true,
    value: currentExternalNote || '',
    variant: "outlined",
    onChange: event => setCurrentExternalNote(event.target.value),
    multiline: true,
    rows: 5,
    backgroundColor: "#FFFFFF",
    placeholder: i18n.t(tokens.labels.changeRequests.addYourNoteHere)
  }), /*#__PURE__*/React.createElement("div", {
    style: styles.noteBox
  }, /*#__PURE__*/React.createElement(Button, {
    transparent: true,
    onClick: () => _closeNote('externalNotes')
  }, i18n.t(tokens.buttons.close)), /*#__PURE__*/React.createElement(Button, {
    transparent: true,
    onClick: () => _saveNote('externalNotes')
  }, i18n.t(tokens.buttons.save)))), /*#__PURE__*/React.createElement(Notes, {
    notes: data.externalNotes,
    allowRemoval: isMaster && !['Approved', 'Declined'].includes(data.status),
    removeNote: (...params) => _removeNote('externalNotes', ...params)
  })), isMaster && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    style: styles.sectionHeader
  }, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    medium: true
  }, i18n.t(tokens.labels.changeRequests.internalNotes)), /*#__PURE__*/React.createElement(Button, {
    transparent: true,
    onClick: () => setShowInternalNote(true)
  }, i18n.t(tokens.labels.changeRequests.addNote))), showInternalNote && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TextInput, {
    fullWidth: true,
    value: currentInternalNote || '',
    variant: "outlined",
    onChange: event => setCurrentInternalNote(event.target.value),
    multiline: true,
    rows: 5,
    backgroundColor: "#FFFFFF",
    placeholder: i18n.t(tokens.labels.changeRequests.addYourNoteHere)
  }), /*#__PURE__*/React.createElement("div", {
    style: styles.noteBox
  }, /*#__PURE__*/React.createElement(Button, {
    transparent: true,
    onClick: () => _closeNote('internalNotes')
  }, i18n.t(tokens.buttons.close)), /*#__PURE__*/React.createElement(Button, {
    transparent: true,
    onClick: () => _saveNote('internalNotes')
  }, i18n.t(tokens.buttons.save)))), /*#__PURE__*/React.createElement(Notes, {
    notes: data.internalNotes,
    allowRemoval: isMaster,
    removeNote: (...params) => _removeNote('internalNotes', ...params)
  })), data.changeRequestUUID ? /*#__PURE__*/React.createElement(Files, {
    isMasterSite: isMaster,
    isLocked: !isMaster && !['Pending Admin Review', 'Pending Member Review', 'Incomplete'].includes(data.status) // Never locked for Master, locked for member/admin when not in pending or incomplete status
    ,
    noContainer: true,
    style: {
      marginTop: '25px'
    },
    dispatch: dispatch,
    type: 'users',
    id: id,
    parentFolder: `changeRequests/${data.changeRequestUUID}/files`,
    listFiles: listFiles,
    createFolder: createFolder,
    uploadFile: uploadFile,
    downloadFile: downloadFile,
    removeFile: removeFile
  }) : null);
}
export default Attachments;